// The kiosk: the assistant as a whole page, opened from a QR code on a
// poster or at a fair. Same chat as the frame (App with kiosk), no launcher.
//
// Two addresses:
// - the client's own host (asystent.edach.eu): every path opens the kiosk of
//   the tenant the host belongs to (tenant_domains); ?key= is ignored there,
//   so nobody can show another assistant under the client's name;
// - our widget host (/kiosk?key=...): previews and clients without a domain.

import { render } from "preact";
import { App } from "../frame/App.tsx";
import { API_URL, setVisit } from "../frame/lib/api.ts";
import { setApiKey } from "../frame/lib/bridge.ts";
import "../frame/theme.css";
import "./kiosk.css";

const params = new URLSearchParams(location.search);
const WIDGET_HOST = new URL(import.meta.env.VITE_WIDGET_ORIGIN).hostname;
// Local development runs on our own host; ?host= pretends to be a client's.
const devHost = import.meta.env.DEV ? params.get("host") : null;
const ownHost = !devHost && (location.hostname === WIDGET_HOST || import.meta.env.DEV);
const app = document.getElementById("app")!;

type Lookup = { key: string } | { error: "missing" | "offline" };

async function lookupKey(): Promise<Lookup> {
  if (ownHost) {
    const key = params.get("key");
    return key ? { key } : { error: "missing" };
  }
  try {
    const res = await fetch(`${API_URL}/kiosk?host=${encodeURIComponent(devHost ?? location.hostname)}`);
    if (res.status === 404 || res.status === 400) return { error: "missing" };
    if (!res.ok) return { error: "offline" };
    return { key: ((await res.json()) as { key: string }).key };
  } catch {
    return { error: "offline" };
  }
}

/** Source of the visit, for the client's statistics: kiosk plus the QR link's UTM. */
function visitMetadata(): Record<string, string> {
  const visit: Record<string, string> = { source: "kiosk" };
  for (const name of ["utm_source", "utm_medium", "utm_campaign"]) {
    const value = params.get(name)?.trim();
    if (value) visit[name] = value.slice(0, 100);
  }
  return visit;
}

/**
 * iOS keeps the layout viewport when the keyboard opens and pans the page
 * instead, pushing the header away. The app follows the visual viewport, so
 * the composer sits on the keyboard and the header stays in place.
 */
function followVisualViewport() {
  const vv = window.visualViewport;
  if (!vv) return;
  const root = document.documentElement;
  const fit = () => {
    root.style.setProperty("--kiosk-height", `${vv.height}px`);
    root.style.setProperty("--kiosk-top", `${vv.offsetTop}px`);
  };
  vv.addEventListener("resize", fit);
  vv.addEventListener("scroll", fit);
  fit();
}

function Unavailable({ offline }: { offline: boolean }) {
  return (
    <div class="kiosk-state">
      <p>{offline ? "Nie udało się połączyć z asystentem. Sprawdź internet i spróbuj ponownie." : "Pod tym adresem nie ma asystenta."}</p>
      {offline && (
        <button type="button" onClick={() => location.reload()}>
          Spróbuj ponownie
        </button>
      )}
    </div>
  );
}

document.documentElement.dataset.kiosk = "";
followVisualViewport();

const lookup = await lookupKey();
if ("key" in lookup) {
  setApiKey(lookup.key);
  setVisit(visitMetadata());
  render(<App kiosk />, app);
} else {
  render(<Unavailable offline={lookup.error === "offline"} />, app);
}
